import type React from 'react';
import { useId } from 'react';
import { useForm } from 'react-final-form';
import FirstNameField from './FirstNameField';
import MiddleNameField from './MiddleNameField';
import LastNameField from './LastNameField';
import AddressField from './AddressField';
import CityField from './CityField';
import StateField from './StateField';
import ZipCodeField from './ZipCodeField';
import DateOfBirthField from './DateOfBirthField';
import PhoneNumberField from './PhoneNumberField';
import SocialSecurityNumberField from './SocialSecurityNumberField';
import { Box, Button, Form, Heading, Paragraph, Popover, TextButton, VisuallyHidden } from '@zillow/constellation-10';
import { usePidAnswers, useTermSet } from '../../contexts/IdVerificationContext';
import { TermSet } from '../../../..';
import PrivacyCard from '../../PrivacyCard';
import { useMount } from 'ahooks';

/* eslint-disable curly-quotes/no-straight-quotes */
const renterTemplateAreaXl = `
  "head head head head head head privacy privacy privacy privacy privacy"
  "subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy"
  "first first first first first first privacy privacy privacy privacy privacy"
  "middle middle middle middle middle middle privacy privacy privacy privacy privacy"
  "last last last last last last privacy privacy privacy privacy privacy"
  "address address address address address address privacy privacy privacy privacy privacy"
  "city city state state zip zip privacy privacy privacy privacy privacy"
  "dob dob dob phone phone phone privacy privacy privacy privacy privacy"
  "ssn ssn ssn . . . privacy privacy privacy privacy privacy"
  "popper popper popper popper popper popper privacy privacy privacy privacy privacy"
`;

const renterTemplateAreaLg = `
  "head head head head head head privacy privacy privacy privacy privacy"
  "subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy"
  "first first first first first first privacy privacy privacy privacy privacy"
  "middle middle middle middle middle middle privacy privacy privacy privacy privacy"
  "last last last last last last privacy privacy privacy privacy privacy"
  "address address address address address address privacy privacy privacy privacy privacy"
  "city   city  city  city  city  city  privacy privacy privacy privacy privacy"
  "state  state state state state state privacy privacy privacy privacy privacy"
  "zip    zip   zip   zip   zip   zip   privacy privacy privacy privacy privacy"
  "dob dob dob phone phone phone privacy privacy privacy privacy privacy"
  "ssn ssn ssn . . . privacy privacy privacy privacy privacy"
  "popper popper popper popper popper popper privacy privacy privacy privacy privacy"
`;

const landlordTemplateArea = `
  "head head head head head head privacy privacy privacy privacy privacy"
  "subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy"
  "first first first first first first privacy privacy privacy privacy privacy"
  "middle middle middle middle middle middle privacy privacy privacy privacy privacy"
  "last last last last last last privacy privacy privacy privacy privacy"
  "address address address address address address privacy privacy privacy privacy privacy"
  "city city state state zip zip privacy privacy privacy privacy privacy"
  "ssn ssn ssn . . . privacy privacy privacy privacy privacy"
  "popper popper popper popper popper popper privacy privacy privacy privacy privacy"
`;

const defaultRenterTemplateArea = `
  "head"
  "subHead"
  "first"
  "middle"
  "last"
  "address"
  "city"
  "state"
  "zip"
  "dob"
  "phone"
  "ssn"
  "popper"
  "privacy"
`;

const defaultLandlordTemplateArea = `
  "head"
  "subHead"
  "first"
  "middle"
  "last"
  "address"
  "city"
  "state"
  "zip"
  "ssn"
  "popper"
  "privacy"
`;
/* eslint-enable curly-quotes/no-straight-quotes */

interface PersonalInfoStepContentProps {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export default function PersonalInfoStepContent({ handleSubmit }: PersonalInfoStepContentProps) {
  const idVerificationId = useId();
  const termSet = useTermSet();
  const pidAnswers = usePidAnswers();
  const form = useForm();

  useMount(() => {
    form.batch(() => {
      Object.entries(pidAnswers).forEach(([fieldName, fieldValue]) => {
        if (fieldValue && String(fieldValue).trim() != '') {
          form.blur(fieldName);
        }
      });
    });
  });

  return (
    <Form onSubmit={handleSubmit}>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(1, 1fr)',
            lg: termSet === TermSet.RenterScreening ? 'repeat(11, 1fr)' : 'repeat(1, 1fr)',
            xl: 'repeat(11, 1fr)',
          },
          gap: 'loose',
          gridTemplateAreas: {
            base: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : defaultRenterTemplateArea,
            lg: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : renterTemplateAreaLg,
            xl: termSet === TermSet.LandlordScreening ? landlordTemplateArea : renterTemplateAreaXl,
          },
        }}
      >
        <Box css={{ gridArea: 'head' }}>
          <Heading level={2} textStyle="heading-sm">
            Provide your details for verification
          </Heading>
        </Box>
        <Box css={{ gridArea: 'subHead' }}>
          <Paragraph>
            {termSet === TermSet.RenterScreening
              ? 'This information will be sent to Experian and CIC to verify your identity and create credit and background reports.'
              : 'This information helps our partners, Experian and CIC, verify your identity so you can review applicants’ information and find a suitable renter. '}
          </Paragraph>
        </Box>
        <Box css={{ gridArea: 'first' }}>
          <FirstNameField />
        </Box>
        <Box css={{ gridArea: 'middle' }}>
          <MiddleNameField />
        </Box>
        <Box css={{ gridArea: 'last' }}>
          <LastNameField />
        </Box>
        <Box css={{ gridArea: 'address' }}>
          <AddressField />
        </Box>
        <Box css={{ gridArea: 'city' }}>
          <CityField />
        </Box>
        <Box css={{ gridArea: 'state' }}>
          <StateField />
        </Box>
        <Box css={{ gridArea: 'zip' }}>
          <ZipCodeField />
        </Box>
        {termSet === TermSet.RenterScreening && (
          <>
            <Box css={{ gridArea: 'dob' }}>
              <DateOfBirthField />
            </Box>
            <Box css={{ gridArea: 'phone' }}>
              <PhoneNumberField />
            </Box>
          </>
        )}
        <Box css={{ gridArea: 'ssn' }}>
          <SocialSecurityNumberField />
        </Box>
        <Box css={{ gridArea: 'privacy' }}>
          <PrivacyCard />
        </Box>
        <Box css={{ gridArea: 'popper' }}>
          <Popover
            trigger={
              <TextButton onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.preventDefault()}>
                Why do we ask for your Social Security number?
              </TextButton>
            }
            header={
              <Heading level={3} textStyle="heading-sm">
                Social Security number (SSN)
              </Heading>
            }
            body={
              <Paragraph>
                Providing an SSN helps verify your identity faster, but is not required. If this field is left blank,
                Experian may need to ask additional questions to verify your identity.
              </Paragraph>
            }
          />
        </Box>
      </Box>
      <VisuallyHidden>
        <Button type="submit" id={`${idVerificationId}-submit-pid`}>
          Submit
        </Button>
      </VisuallyHidden>
    </Form>
  );
}
